// Shopify Product Reviews App - Front-end Script
// This script handles product reviews display and submission

class ProductReviews {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.shopId = container.dataset.shopId;
    this.apiUrl = container.dataset.apiUrl || '';
    
    // Filter and sort selectors
    this.starFilter = container.querySelector('.star-filter');
    this.sortOptions = container.querySelector('.sort-options');
    
    // Review display elements
    this.reviewsList = container.querySelector('.reviews-list');
    this.paginationContainer = container.querySelector('.reviews-pagination');
    
    // Stats elements
    this.ratingValue = container.querySelector('.rating-value');
    this.reviewsCount = container.querySelector('.reviews-count');
    this.starsContainer = container.querySelector('.stars-container');
    
    // Write review button
    this.writeReviewButton = container.querySelector('.write-review-button');
    
    // Review template
    this.reviewTemplate = document.getElementById('review-template');
    
    // State
    this.currentPage = 1;
    this.totalPages = 0;
    this.currentStars = '';
    this.currentSort = 'recent';
    
    // Initialize
    this.init();
  }
  
  init() {
    // Load reviews on page load
    this.loadReviews();
    
    // Set up event listeners
    this.setupEventListeners();
  }
  
  setupEventListeners() {
    // Filter by stars
    if (this.starFilter) {
      this.starFilter.addEventListener('change', () => {
        this.currentStars = this.starFilter.value;
        this.currentPage = 1;
        this.loadReviews();
      });
    }
    
    // Sort reviews
    if (this.sortOptions) {
      this.sortOptions.addEventListener('change', () => {
        this.currentSort = this.sortOptions.value;
        this.currentPage = 1;
        this.loadReviews();
      });
    }
    
    // Write review button
    if (this.writeReviewButton) {
      this.writeReviewButton.addEventListener('click', () => {
        // Scroll to review form or show modal
        const reviewForm = document.querySelector('.review-form-container');
        if (reviewForm) {
          reviewForm.scrollIntoView({ behavior: 'smooth' });
          
          // Focus first field
          const firstInput = reviewForm.querySelector('input, textarea');
          if (firstInput) {
            setTimeout(() => {
              firstInput.focus();
            }, 500);
          }
        }
      });
    }
  }
  
  async loadReviews() {
    try {
      this.showLoading(true);
      
      // Build API URL
      const url = new URL(`${this.apiUrl}/api/public/reviews`);
      
      // Add query params
      url.searchParams.append('shop', this.shopId);
      url.searchParams.append('productId', this.productId);
      url.searchParams.append('page', this.currentPage);
      
      if (this.currentStars) {
        url.searchParams.append('stars', this.currentStars);
      }
      
      if (this.currentSort) {
        url.searchParams.append('sort', this.currentSort);
      }
      
      console.log('Fetching reviews from:', url.toString());
      
      // Fetch reviews
      const response = await fetch(url.toString());
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Review API error:', errorText);
        throw new Error('Failed to load reviews');
      }
      
      const data = await response.json();
      console.log('Received reviews data:', data);
      
      // Update state
      this.totalPages = data.pagination.pages;
      
      // Render reviews
      this.renderReviews(data.reviews);
      
      // Update stats
      this.updateStats(data.stats);
      
      // Render pagination
      this.renderPagination();
      
      // Show content
      this.showLoading(false);
    } catch (error) {
      console.error('Error loading reviews:', error);
      this.renderError('Unable to load reviews. Please try again later.');
    }
  }
  
  renderReviews(reviews) {
    // Clear reviews list
    this.reviewsList.innerHTML = '';
    
    if (!reviews || reviews.length === 0) {
      const noReviews = document.createElement('div');
      noReviews.className = 'no-reviews';
      noReviews.textContent = 'No reviews yet. Be the first to review this product!';
      this.reviewsList.appendChild(noReviews);
      return;
    }
    
    // Loop through reviews and render each one
    reviews.forEach(review => {
      const reviewElement = this.createReviewElement(review);
      this.reviewsList.appendChild(reviewElement);
    });
  }
  
  createReviewElement(review) {
    // Clone the template
    const reviewElement = document.importNode(this.reviewTemplate.content, true).firstElementChild;
    
    // Fill in the review content
    reviewElement.querySelector('.review-title').textContent = review.title;
    reviewElement.querySelector('.review-text').textContent = review.description;
    
    // Format date
    const date = new Date(review.displayDate);
    reviewElement.querySelector('.review-date').textContent = date.toLocaleDateString();
    
    // Set reviewer name
    reviewElement.querySelector('.reviewer-name').textContent = review.userName;
    
    // Set verified status
    if (review.isVerifiedPurchaser) {
      const verified = document.createElement('span');
      verified.className = 'verified-badge';
      verified.textContent = 'Verified Purchase';
      reviewElement.querySelector('.review-verified').appendChild(verified);
    }
    
    // Render stars
    const starsContainer = reviewElement.querySelector('.review-stars');
    starsContainer.innerHTML = this.generateStars(review.rating);
    
    // Render photos if any
    if (review.photos && review.photos.length > 0) {
      const photosContainer = reviewElement.querySelector('.review-photos');
      
      review.photos.forEach(photo => {
        const img = document.createElement('img');
        img.src = photo.url;
        img.alt = 'Review photo';
        img.className = 'review-photo';
        
        // Wrap in clickable link for lightbox
        const link = document.createElement('a');
        link.href = photo.url;
        link.className = 'review-photo-link';
        link.setAttribute('data-lightbox', `review-${review._id}`);
        link.appendChild(img);
        
        photosContainer.appendChild(link);
      });
    }
    
    return reviewElement;
  }
  
  renderPagination() {
    // Clear pagination
    this.paginationContainer.innerHTML = '';
    
    if (this.totalPages <= 1) {
      return; // No need for pagination
    }
    
    const paginationList = document.createElement('ul');
    paginationList.className = 'pagination';
    
    // Previous button
    if (this.currentPage > 1) {
      const prevButton = this.createPaginationButton('Prev', this.currentPage - 1);
      paginationList.appendChild(prevButton);
    }
    
    // Page numbers
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + 4);
    
    // Adjust start if we're near the end
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      const pageButton = this.createPaginationButton(i.toString(), i);
      
      if (i === this.currentPage) {
        pageButton.classList.add('active');
      }
      
      paginationList.appendChild(pageButton);
    }
    
    // Next button
    if (this.currentPage < this.totalPages) {
      const nextButton = this.createPaginationButton('Next', this.currentPage + 1);
      paginationList.appendChild(nextButton);
    }
    
    this.paginationContainer.appendChild(paginationList);
  }
  
  createPaginationButton(text, page) {
    const li = document.createElement('li');
    const button = document.createElement('button');
    button.textContent = text;
    button.className = 'pagination-button';
    button.setAttribute('data-page', page);
    
    button.addEventListener('click', () => {
      this.currentPage = page;
      this.loadReviews();
      
      // Scroll to reviews section
      this.container.scrollIntoView({ behavior: 'smooth' });
    });
    
    li.appendChild(button);
    return li;
  }
  
  updateStats(stats) {
    if (!stats) return;
    
    // Update average rating
    if (this.ratingValue) {
      this.ratingValue.textContent = stats.averageRating.toFixed(1);
    }
    
    // Update review count
    if (this.reviewsCount) {
      this.reviewsCount.textContent = stats.totalReviews;
    }
    
    // Update stars display
    if (this.starsContainer) {
      this.starsContainer.innerHTML = this.generateStars(stats.averageRating);
    }
    
    // Update star breakdown if available
    const starBreakdown = this.container.querySelector('.star-breakdown');
    if (starBreakdown) {
      const levels = starBreakdown.querySelectorAll('.star-level');
      
      if (levels.length === 5) {
        const total = stats.totalReviews || 1; // Avoid division by zero
        
        // Update percentages
        this.updateStarLevel(levels[0], stats.fiveStars, total);
        this.updateStarLevel(levels[1], stats.fourStars, total);
        this.updateStarLevel(levels[2], stats.threeStars, total);
        this.updateStarLevel(levels[3], stats.twoStars, total);
        this.updateStarLevel(levels[4], stats.oneStars, total);
      }
    }
  }
  
  updateStarLevel(element, count, total) {
    const percent = Math.round((count / total) * 100);
    const percentEl = element.querySelector('.star-percent');
    const fillEl = element.querySelector('.progress-fill');
    
    if (percentEl) percentEl.textContent = `${percent}%`;
    if (fillEl) fillEl.style.width = `${percent}%`;
  }
  
  generateStars(rating) {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
    
    let starsHtml = '';
    
    // Full stars
    for (let i = 0; i < fullStars; i++) {
      starsHtml += '<span class="star star-full">★</span>';
    }
    
    // Half star
    if (halfStar) {
      starsHtml += '<span class="star star-half">★</span>';
    }
    
    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      starsHtml += '<span class="star star-empty">★</span>';
    }
    
    return starsHtml;
  }
  
  showLoading(isLoading) {
    const loadingElement = this.reviewsList.querySelector('.reviews-loading');
    
    if (isLoading) {
      // Show loading
      if (!loadingElement) {
        const loading = document.createElement('div');
        loading.className = 'reviews-loading';
        loading.textContent = 'Loading reviews...';
        this.reviewsList.innerHTML = '';
        this.reviewsList.appendChild(loading);
      }
    } else {
      // Hide loading
      if (loadingElement) {
        loadingElement.remove();
      }
    }
  }
  
  renderError(message) {
    this.reviewsList.innerHTML = '';
    
    const errorElement = document.createElement('div');
    errorElement.className = 'reviews-error';
    errorElement.textContent = message;
    
    this.reviewsList.appendChild(errorElement);
  }
}

// Initialize reviews on page load
document.addEventListener('DOMContentLoaded', () => {
  const reviewsContainers = document.querySelectorAll('.product-reviews-container');
  
  reviewsContainers.forEach(container => {
    new ProductReviews(container);
  });
});

// Re-initialize when Shopify section is reloaded (theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.product-reviews-container');
    if (container) {
      new ProductReviews(container);
    }
  });
} 